import React, { useState, useEffect, useCallback } from 'react';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import {
  getPendingCount,
  syncQueue,
  QUEUE_UPDATED_EVENT,
} from '../services/offlineQueue';

export default function OfflineBanner() {
  const isOnline = useNetworkStatus();
  const [pending, setPending] = useState(getPendingCount);
  const [syncing, setSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Atualiza contagem sempre que a fila muda
  useEffect(() => {
    const onUpdate = () => {
      setPending(getPendingCount());
      setDismissed(false); // reaparece se novos itens entram na fila
    };
    window.addEventListener(QUEUE_UPDATED_EVENT, onUpdate);
    return () => window.removeEventListener(QUEUE_UPDATED_EVENT, onUpdate);
  }, []);

  // Quando volta online, reexibe (para mostrar sync) e depois some
  useEffect(() => {
    if (isOnline) {
      setDismissed(false);
      // Se não há pendentes, some automaticamente após 2s (tempo para o usuário ver)
      if (getPendingCount() === 0) {
        const t = setTimeout(() => setDismissed(true), 2000);
        return () => clearTimeout(t);
      }
    } else {
      // Volta a aparecer quando fica offline
      setDismissed(false);
    }
  }, [isOnline]);

  const runSync = useCallback(async () => {
    if (syncing) return;
    const current = getPendingCount();
    if (current === 0) return;
    setSyncing(true);
    const { synced } = await syncQueue();
    setSyncing(false);
    setPending(getPendingCount());
    if (synced > 0) {
      setJustSynced(true);
      setTimeout(() => setJustSynced(false), 3000);
    }
  }, [syncing]);

  useEffect(() => {
    if (isOnline) runSync();
  }, [isOnline, runSync]);

  // Não mostra nada quando online sem pendentes, ou usuário fechou
  if ((isOnline && pending === 0 && !justSynced) || dismissed) return null;

  let bg = '#e65100';
  if (syncing) bg = '#1565c0';
  else if (justSynced) bg = '#2e7d32';
  else if (isOnline && pending > 0) bg = '#f57f17';

  let icon = '📵';
  if (syncing) icon = '⏳';
  else if (justSynced) icon = '✅';
  else if (isOnline) icon = '⚠️';

  let msg = 'Você está offline';
  if (syncing) msg = 'Sincronizando...';
  else if (justSynced) msg = 'Dados sincronizados!';
  else if (!isOnline && pending > 0) msg = `Offline — ${pending} pendente${pending > 1 ? 's' : ''}`;
  else if (isOnline && pending > 0) msg = `${pending} item${pending > 1 ? 's' : ''} para sincronizar`;

  return (
    <div style={{
      position: 'fixed',
      bottom: 16,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '6px 12px 6px 14px',
      backgroundColor: bg,
      color: '#fff',
      fontSize: 12,
      fontWeight: 600,
      borderRadius: 20,
      boxShadow: '0 3px 12px rgba(0,0,0,0.35)',
      whiteSpace: 'nowrap',
      maxWidth: 'calc(100vw - 32px)',
    }}>
      <span style={{ fontSize: 14 }}>{icon}</span>
      <span style={{ flex: 1 }}>{msg}</span>
      {isOnline && pending > 0 && !syncing && (
        <button onClick={runSync} style={{
          background: 'rgba(255,255,255,0.25)',
          border: '1px solid rgba(255,255,255,0.5)',
          color: '#fff',
          borderRadius: 10,
          padding: '2px 8px',
          cursor: 'pointer',
          fontSize: 11,
          fontWeight: 700,
          marginLeft: 4,
        }}>
          Enviar
        </button>
      )}
      {!syncing && !justSynced && (
        <button onClick={() => setDismissed(true)} style={{
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,0.8)',
          cursor: 'pointer',
          fontSize: 16,
          lineHeight: 1,
          padding: '0 0 0 4px',
          fontWeight: 700,
        }}>
          ✕
        </button>
      )}
    </div>
  );
}

